import type { Piece, Team } from '@halvard/rules';
import { useEffect, useRef, type CSSProperties, type KeyboardEvent } from 'react';
import { moveCursor, screenOrder } from './keyboard.js';
import { squareLabel } from './labels.js';
import type { BoardHint, Square, SummitMark } from './types.js';

/** Hidden from sight but available to keyboards and screen readers. */
const visuallyHidden: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * Every cell sits on the same spot inside the hidden grid, so focusing any of them never makes
 * the page or an ancestor scroll.
 */
const hiddenCell: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 1,
  height: 1,
  overflow: 'hidden',
};

export interface BoardGridProps {
  heights: number[][];
  pieces: readonly Piece[];
  ply: number;
  hints: readonly BoardHint[];
  visible: boolean[][] | null;
  summits: readonly SummitMark[];
  selectedId: string | null;
  cursor: Square;
  facing: Team;
  label: string;
  onCursorChange: (square: Square) => void;
  onActivate: (square: Square) => void;
  onCancel: () => void;
  onFocusChange: (focused: boolean) => void;
}

/**
 * An ARIA grid mirroring the 3D board: arrow keys move the cursor (shown in 3D), Enter or Space
 * activates the square, Escape cancels. Each cell is labelled, e.g. "Ivory Climber on c1, height 1".
 */
export function BoardGrid(props: BoardGridProps) {
  const cells = useRef(new Map<string, HTMLDivElement>());
  const hasFocus = useRef(false);
  const order = screenOrder(props.facing);

  useEffect(() => {
    if (hasFocus.current)
      cells.current.get(`${props.cursor.r}:${props.cursor.c}`)?.focus({ preventScroll: true });
  }, [props.cursor]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const next = moveCursor(props.cursor, event.key, props.facing);
    if (next) {
      event.preventDefault();
      props.onCursorChange(next);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      props.onActivate(props.cursor);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      props.onCancel();
    }
  };

  return (
    <div
      role="grid"
      aria-label={props.label}
      style={visuallyHidden}
      onKeyDown={onKeyDown}
      onFocus={() => {
        hasFocus.current = true;
        props.onFocusChange(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          hasFocus.current = false;
          props.onFocusChange(false);
        }
      }}
    >
      {order.map((r) => (
        <div role="row" key={r}>
          {order.map((c) => {
            const active = props.cursor.r === r && props.cursor.c === c;
            return (
              <div
                key={c}
                role="gridcell"
                style={hiddenCell}
                tabIndex={active ? 0 : -1}
                aria-selected={props.pieces.some(
                  (p) => p.alive && p.id === props.selectedId && p.r === r && p.c === c,
                )}
                ref={(el) => {
                  if (el) cells.current.set(`${r}:${c}`, el);
                  else cells.current.delete(`${r}:${c}`);
                }}
                onFocus={() => {
                  if (!active) props.onCursorChange({ r, c });
                }}
                onClick={() => props.onActivate({ r, c })}
              >
                {squareLabel({
                  r,
                  c,
                  heights: props.heights,
                  pieces: props.pieces,
                  ply: props.ply,
                  hints: props.hints,
                  selectedId: props.selectedId,
                  visible: props.visible,
                  summits: props.summits,
                })}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
